import React, { useState, useEffect } from 'react';
import { 
  Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Badge, Spinner, AlertDialog, AlertDialogContent, 
  AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../ui/UIComponents';
import { Plus, Edit, Trash2, Key, Settings, AlertCircle, Lock, ChevronDown, Layers } from 'lucide-react';
import { Permission, Application } from '../../types/rbac.types';
import { getPermissions, deletePermission } from '../../services/rbacApi';
import { getApplications } from '../../services/rbacApi';
import PermissionForm from './PermissionForm';
import TreeView, { TreeNode } from '../ui/TreeView';

const PermissionList: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [currentPermission, setCurrentPermission] = useState<Permission | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [permissionToDelete, setPermissionToDelete] = useState<Permission | null>(null);

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Fetch permissions when selected application changes
  useEffect(() => {
    if (selectedAppId) {
      fetchPermissions(selectedAppId);
    }
  }, [selectedAppId]);

  const fetchApplications = async () => {
    try {
      const data = await getApplications();
      // Applications fetched successfully
      setApplications(data);
      
      // Select the first active application by default
      const activeApp = data.find(app => app.status === 'active');
      if (activeApp) {
        // Setting default application
        setSelectedAppId(activeApp.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch applications');
      console.error('Error fetching applications:', err);
    }
  };

  const fetchPermissions = async (applicationId: string) => {
    try {
      setLoading(true);
      const data = await getPermissions(applicationId);
      setPermissions(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = (appId: string) => {
    // Application selected
    setSelectedAppId(appId);
  };
  
  // Convert applications to tree nodes
  const getApplicationTreeNodes = (): TreeNode[] => {
    return applications.map(app => ({
      id: app.id,
      name: app.name,
      icon: <Layers size={18} className="text-blue-400" />,
      data: app,
      badge: app.status === 'active' ? (
        <Badge variant="secondary" className="bg-green-900/30 text-green-300 border border-green-700/30 text-xs">
          Active
        </Badge>
      ) : app.status === 'development' ? (
        <Badge variant="secondary" className="bg-amber-900/30 text-amber-300 border border-amber-700/30 text-xs">
          Dev
        </Badge>
      ) : (
        <Badge variant="secondary" className="bg-gray-900/30 text-gray-300 border border-gray-700/30 text-xs">
          Inactive
        </Badge>
      )
    }));
  };

  const handleEdit = (permission: Permission) => {
    setCurrentPermission(permission);
    setIsFormOpen(true);
  };

  const handleDelete = (permission: Permission) => {
    setPermissionToDelete(permission);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!permissionToDelete) return;
    
    try {
      await deletePermission(permissionToDelete.id);
      // Refresh the permissions list
      fetchPermissions(selectedAppId);
      setIsDeleteDialogOpen(false);
      setPermissionToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete permission');
      console.error('Error deleting permission:', err);
    }
  };

  const handleFormClose = (refreshData: boolean = false) => {
    setIsFormOpen(false);
    setCurrentPermission(null);
    if (refreshData && selectedAppId) {
      fetchPermissions(selectedAppId);
    }
  };

  // Find the selected application name
  const selectedAppName = applications.find(app => app.id === selectedAppId)?.name || '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Permissions</h2>
          <p className="text-gray-400 text-sm mt-1">Manage access control permissions</p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => setIsFormOpen(true)}
          disabled={!selectedAppId}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
        >
          <Plus size={16} className="mr-1.5" />
          Add Permission
        </Button>
      </div>

      <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 p-0.5 rounded-lg overflow-hidden">
        <div className="bg-background-card rounded-lg p-5">
          <label className="block text-sm font-medium text-blue-300 mb-2 flex items-center">
            <Settings size={16} className="mr-2" />
            Select Application
          </label>
          <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/50">
            <TreeView 
              nodes={getApplicationTreeNodes()} 
              onSelect={(node) => handleApplicationChange(node.id)}
              selectedId={selectedAppId}
              className="max-h-[250px] overflow-y-auto py-1.5"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-center space-x-2 animate-fadeIn">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {!selectedAppId ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-b from-gray-900/30 to-gray-900/10 rounded-xl border border-gray-800/50">
          <div className="bg-blue-900/20 p-4 rounded-full border border-blue-500/20 mb-4">
            <Key className="h-10 w-10 text-blue-400/80" />
          </div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">Select an Application</h3>
          <p className="text-gray-400 mb-6 max-w-md text-center">Choose an application from the dropdown above to view and manage its permissions.</p>
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 right-0 h-16 w-16 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute top-1 right-1 h-14 w-14 rounded-full border-4 border-t-transparent border-r-indigo-500 border-b-transparent border-l-transparent animate-spin-slow"></div>
          </div>
          <p className="text-gray-400 mt-4">Loading permissions...</p>
        </div>
      ) : permissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-b from-gray-900/30 to-gray-900/10 rounded-xl border border-gray-800/50">
          <div className="bg-blue-900/20 p-4 rounded-full border border-blue-500/20 mb-4">
            <Lock className="h-10 w-10 text-blue-400/80" />
          </div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">No permissions found</h3>
          <p className="text-gray-400 mb-6 max-w-md text-center">
            No permissions have been created for <span className="text-blue-300 font-medium">{selectedAppName}</span> yet.
          </p>
          <Button 
            variant="primary" 
            onClick={() => setIsFormOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
          >
            <Plus size={16} className="mr-1.5" />
            Add your first permission
          </Button>
        </div>
      ) : (
        <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 p-0.5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto bg-background-card rounded-xl">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="font-medium text-gray-300">Name</TableCell>
                  <TableCell className="font-medium text-gray-300">Code</TableCell>
                  <TableCell className="font-medium text-gray-300">Category</TableCell>
                  <TableCell className="font-medium text-gray-300">Description</TableCell>
                  <TableCell className="font-medium text-gray-300">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((permission) => (
                  <TableRow key={permission.id} className="transition-colors hover:bg-gray-800/30">
                    <TableCell className="font-medium text-blue-300">{permission.name}</TableCell>
                    <TableCell>
                      <code className="bg-gray-800/80 px-2 py-1 rounded text-sm text-indigo-300 font-mono border border-indigo-900/20">
                        {permission.code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="primary" className="bg-purple-900/30 text-purple-300 border border-purple-700/30">
                        {permission.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-gray-400">{permission.description || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="xs" 
                          onClick={() => handleEdit(permission)}
                          title="Edit"
                          className="border-blue-800/30 bg-blue-900/20 hover:bg-blue-800/30 text-blue-300"
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="xs"
                          onClick={() => handleDelete(permission)}
                          title="Delete"
                          className="border-red-800/30 bg-red-900/20 hover:bg-red-800/30 text-red-300"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Permission Form Modal */}
      <PermissionForm 
        isOpen={isFormOpen} 
        onClose={handleFormClose} 
        permission={currentPermission} 
        applicationId={selectedAppId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-100">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will delete the permission "{permissionToDelete?.name}". 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PermissionList; 